import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Stack } from '@lyearn/ui';
import { SetFieldValue } from '@/components/Form';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import { EventStatusEnum, EventSubTypeEnum, RecordingSettingsInput } from '@/types/schema';

import { RecordingSettingsFields } from './consts';

function RecordingSettings({
  recordingSettings,
  subType,
  setFieldValue,
  fieldKey,
  status,
}: {
  recordingSettings?: RecordingSettingsInput;
  subType?: EventSubTypeEnum;
  setFieldValue: SetFieldValue;
  fieldKey: string;
  status?: EventStatusEnum;
}) {
  const { t } = useTranslation();
  const isWebinarSubType = subType === EventSubTypeEnum.Webinar;

  const onChange = useCallback(
    (field: RecordingSettingsFields, value: boolean) => {
      setFieldValue(`${fieldKey}.${field}`, value, true);
    },
    [fieldKey, setFieldValue],
  );

  const onToggleBackgroundVideoPlayback = useCallback(
    (value: boolean) => {
      onChange(RecordingSettingsFields.ALLOW_BACKGROUND_VIDEO_PLAYBACK, value);
    },
    [onChange],
  );

  const handleAllowForwardingChange = (value: boolean) => {
    // When allowForwarding is enabled, allowPlaybackSpeedControl is "true" by default and its toggle hidden
    // When allowForwarding is disabled, allowPlaybackSpeedControl toggle is shown and initialised with a "false" value
    onChange(RecordingSettingsFields.ALLOW_FORWARDING, value);
    if (value) {
      onChange(RecordingSettingsFields.ALLOW_PLAYBACK_SPEED_CONTROL, true);
    } else {
      onChange(RecordingSettingsFields.ALLOW_PLAYBACK_SPEED_CONTROL, false);
    }
  };

  return (
    <Stack className="px-8" direction="col" gapY={24}>
      <SwitchWithInfo
        checked={Boolean(isWebinarSubType || recordingSettings?.automaticRecording)}
        description={
          isWebinarSubType
            ? t('Events:Editor.AutomaticallyRecordWebinarDescription')
            : t('Events:Editor.AutomaticallyRecordDescription')
        }
        disabled={isWebinarSubType || status === EventStatusEnum.Completed}
        label={t('Events:Editor.AutomaticallyRecord')}
        onChange={(value: boolean) => onChange(RecordingSettingsFields.AUTOMATIC_RECORDING, value)}
      />
      <SwitchWithInfo
        checked={Boolean(recordingSettings?.shareRecording)}
        description={t('Events:Editor.ShareRecordingWithLearnersDescription')}
        label={t('Events:Editor.ShareRecordingWithLearners')}
        onChange={(value: boolean) => onChange(RecordingSettingsFields.SHARE_RECORDING, value)}
      />
      <SwitchWithInfo
        checked={Boolean(recordingSettings?.allowForwarding)}
        description={t('Events:Editor.AllowForwardingDescription')}
        label={t('Events:Editor.AllowForwarding')}
        onChange={handleAllowForwardingChange}
      />
      {Boolean(recordingSettings?.allowForwarding) ? null : (
        <SwitchWithInfo
          checked={Boolean(recordingSettings?.allowPlaybackSpeedControl)}
          description={t('Events:Editor.AllowPlaybackSpeedControlDescription')}
          label={t('Events:Editor.AllowPlaybackSpeedControl')}
          onChange={(value: boolean) =>
            onChange(RecordingSettingsFields.ALLOW_PLAYBACK_SPEED_CONTROL, value)
          }
        />
      )}

      <SwitchWithInfo
        checked={Boolean(recordingSettings?.allowBackgroundVideoPlayback)}
        description={t('Events:Editor.AllowBackgroundVideoPlayback')}
        label={t('Events:Editor.AllowBackgroundVideoPlaybackDescription')}
        onChange={onToggleBackgroundVideoPlayback}
      />
    </Stack>
  );
}

export default React.memo(RecordingSettings);
